<template>
  <div class="global-ai-chat">
    <div class="global-ai-chat-header">
      <div class="global-ai-chat-header-left">
        <h4 class="global-ai-chat-title">AI chat</h4>
        <button
          v-if="conversationHistory.length > 0"
          class="global-ai-text-btn"
          @click="startNewChat"
          :disabled="isStreaming"
          title="Start new chat">
          <span class="material-symbols-outlined">edit_square</span>
          <span>New chat</span>
        </button>
      </div>
      <div class="global-ai-chat-header-right">
        <button
          class="global-ai-icon-btn"
          @click="$emit('expand')"
          title="Toggle expand">
          <span class="material-symbols-outlined">
            {{ isExpanded ? "close_fullscreen" : "open_in_full" }}
          </span>
        </button>
        <button
          class="global-ai-icon-btn"
          @click="$emit('close')"
          title="Close">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>
    </div>

    <div class="global-ai-chat-body" ref="chatBody">
      <div
        v-if="conversationHistory.length === 0 && !isStreaming"
        class="global-ai-welcome">
        <h3 class="global-ai-greeting">
          <StarIcon
            class="global-ai-greeting-icon"
            size="48"
            color="#9720EB"
            opacity="1" />
          <span class="gradient-text">
            Hello {{ mainStore.userData?.firstname }},
          </span>
        </h3>
        <p>How can I help you?</p>
        <div class="global-ai-welcome-input">
          <div
            class="global-ai-input-wrapper"
            :class="{ 'global-ai-input-wrapper--has-content': hasInput }">
            <textarea
              ref="textarea"
              v-model="promptText"
              placeholder="Ask AI"
              @keydown.enter="handleEnter"
              :disabled="isStreaming"
              rows="1"></textarea>
            <button
              class="global-ai-send-btn"
              :disabled="!canSend"
              @click="sendMessage">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8 13V3M8 3L3 8M8 3L13 8"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </button>
          </div>
        </div>
        <div class="global-ai-prompt-templates">
          <button
            v-for="(template, index) in promptTemplates"
            :key="index"
            class="global-ai-prompt-template"
            @click="usePromptTemplate(template.prompt)">
            <StarIcon
              class="global-ai-prompt-template-icon"
              size="20"
              color="#9720EB"
              :opacity="0.3" />
            <span class="global-ai-prompt-template-text">
              {{ template.title }}
            </span>
          </button>
        </div>
      </div>

      <div
        v-if="conversationHistory.length > 0 || isStreaming"
        class="global-ai-messages">
        <div
          v-html="errorMessage"
          v-if="errorMessage !== ''"
          class="alert alert-danger alert-dismissible fade show p-2"></div>

        <div
          v-for="(message, index) in conversationHistory"
          :key="index"
          class="ai-chat-message"
          :class="[
            message.role,
            { 'ai-chat-message--user': message.role === 'user' },
          ]">
          <template v-if="message.role === 'user'">
            <div class="ai-chat-user-bubble">
              {{ message.content }}
            </div>
          </template>
          <template v-else>
            <div class="ai-chat-assistant-content">
              <div
                class="ai-chat-wrapper-content"
                v-html="renderMarkdown(message.content)"></div>
              <div class="action-buttons-row">
                <button
                  class="ai-action-chip ai-action-chip--copy"
                  @click="copyToClipboard(message.content)"
                  title="Copy">
                  <span class="material-symbols-outlined">content_copy</span>
                  <span class="ai-action-chip__title">Copy</span>
                </button>
                <button
                  v-for="(action, aidx) in extractActions(message.content)"
                  :key="aidx"
                  class="ai-action-chip"
                  :class="`ai-action-chip--${action.type}`"
                  @click="openActionModal(action, conversationHistory)">
                  <span class="material-symbols-outlined">
                    {{ actionButtonMaterialIcon(action.type) }}
                  </span>
                  <span class="ai-action-chip__prefix">
                    {{ actionButtonPrefix(action.type) }}:
                  </span>
                  <span class="ai-action-chip__title">
                    {{ actionButtonTitle(action) }}
                  </span>
                </button>
              </div>
            </div>
          </template>
        </div>

        <div
          class="ai-chat-message ai-chat-thinking"
          v-if="isStreaming && !currentToolCall && !streamingContent">
          <div class="ai-chat-thinking-indicator">
            <div class="ai-chat-thinking-dots"><span></span></div>
            <span class="ai-chat-thinking-text">
              {{ loadingMessages[currentLoadingIndex] }}
            </span>
          </div>
          <div class="ai-chat-skeleton-lines">
            <div class="ai-chat-skeleton-line" style="width: 90%"></div>
            <div class="ai-chat-skeleton-line" style="width: 80%"></div>
            <div class="ai-chat-skeleton-line" style="width: 90%"></div>
            <div class="ai-chat-skeleton-line" style="width: 70%"></div>
          </div>
        </div>

        <div v-if="currentToolCall" class="ai-chat-message ai-chat-thinking">
          <div class="ai-chat-thinking-indicator">
            <div class="ai-chat-thinking-dots"><span></span></div>
            <span>{{ currentToolCall }}</span>
          </div>
          <div class="ai-chat-skeleton-lines">
            <div class="ai-chat-skeleton-line" style="width: 90%"></div>
            <div class="ai-chat-skeleton-line" style="width: 80%"></div>
            <div class="ai-chat-skeleton-line" style="width: 90%"></div>
            <div class="ai-chat-skeleton-line" style="width: 70%"></div>
          </div>
        </div>

        <div
          v-if="isStreaming && !currentToolCall && streamingContent"
          class="ai-chat-message">
          <div class="ai-chat-assistant-content">
            <div
              class="ai-chat-wrapper-content"
              v-html="renderMarkdown(streamingContent)"></div>
            <span class="typing-cursor">|</span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="conversationHistory.length > 0 || isStreaming"
      class="global-ai-chat-input">
      <div
        class="global-ai-input-wrapper"
        :class="{ 'global-ai-input-wrapper--has-content': hasInput }">
        <textarea
          ref="textarea"
          v-model="promptText"
          placeholder="Ask AI"
          @keydown.enter="handleEnter"
          :disabled="isStreaming"
          rows="1"></textarea>
        <button
          class="global-ai-send-btn"
          :disabled="!canSend"
          @click="sendMessage">
          <span class="material-symbols-outlined">arrow_upward_alt</span>
        </button>
      </div>
    </div>

    <ActionModals
      v-model:draftEmailVisible="draftEmailVisible"
      v-model:createTaskVisible="createTaskVisible"
      v-model:createDealVisible="createDealVisible"
      v-model:saveNoteVisible="saveNoteVisible"
      v-model:actionSuccessVisible="actionSuccessVisible"
      :draftEmailLoading="draftEmailLoading"
      :draftEmailContent="draftEmailContent"
      :taskForm="taskForm"
      :taskFormCustomerName="taskFormCustomerName"
      :dealForm="dealForm"
      :dealFormCustomerName="dealFormCustomerName"
      :noteForm="noteForm"
      :actionLoading="actionLoading"
      :actionSuccessUrl="actionSuccessUrl"
      :renderMarkdown="renderMarkdown"
      @update:taskForm="taskForm = $event"
      @update:dealForm="dealForm = $event"
      @update:noteForm="noteForm = $event"
      @submitCreateTask="submitCreateTask"
      @submitCreateDeal="submitCreateDeal"
      @submitSaveNote="submitSaveNote"
      @copyToClipboard="copyToClipboard" />

    <Transition name="global-ai-confirm-fade">
      <div
        v-if="showNewChatConfirm"
        class="global-ai-confirm-overlay"
        @click.self="showNewChatConfirm = false">
        <div class="global-ai-confirm-box">
          <div class="global-ai-confirm-header">
            <span class="global-ai-confirm-title">Confirmation</span>
            <button
              class="global-ai-confirm-close"
              @click="showNewChatConfirm = false">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
          <p class="global-ai-confirm-body">
            Starting a new AI chat will delete your current AI chat. This action
            cannot be undone.
          </p>
          <div class="global-ai-confirm-actions">
            <button
              class="global-ai-confirm-btn"
              @click="showNewChatConfirm = false">
              Cancel
            </button>
            <button class="global-ai-confirm-btn" @click="confirmNewChat">
              Start a new chat
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import { useMainStore } from "../../stores/main";
import { useActionModals } from "../../composables/useActionModals";
import { useAiChat } from "../../composables/useAiChat";
import StarIcon from "../icons/StarIcon.vue";
import ActionModals from "../ActionModals.vue";
import {
  fetchGlobalChatHistory,
  clearGlobalChat,
} from "../../assets/js/impactdriver_front/requests";

// 4 lines × line-height 1.4 × 14px = 78.4px; +6px slack for browser-implicit textarea padding.
const TEXTAREA_MAX_HEIGHT = 84;

export default {
  name: "GlobalAIChat",
  emits: ["close", "expand"],
  props: { isExpanded: { type: Boolean, default: false } },
  data() {
    return {
      promptText: "",
      mainStore: useMainStore(),
      showNewChatConfirm: false,
      promptTemplates: [
        {
          title: "Which accounts have high revenue risk?",
          prompt:
            "Identify all customers currently showing churn or retention risk signals. Rank them by highest Revenue first. For each customer, explain why they are considered at risk based on recent engagement patterns, sentiment, product usage, support interactions, renewal timing, stakeholder engagement, or other relevant signals. Highlight the most urgent risks and recommend the next best action for the CSM.",
        },
        {
          title: "Where are my biggest upsell opportunities?",
          prompt:
            "Analyze my customer base and identify customers with the strongest upsell or expansion potential. Prioritize customers based on expansion likelihood and potential revenue impact. Explain the signals behind the recommendation, such as product adoption trends, positive sentiment, feature requests, stakeholder growth, usage increases, or commercial discussions. Suggest the best next action to drive expansion.",
        },
        {
          title: "Which customers would make great case studies?",
          prompt:
            "Identify the best customers to approach for a case study, testimonial, or reference call. Look for customers with strong product adoption, positive sentiment, measurable outcomes, executive engagement, successful milestones, or strategic partnerships. Explain why each customer is a strong candidate and include supporting evidence from recent engagements and account history.",
        },
        {
          title: "What should I focus on this week?",
          prompt:
            "Review my customer portfolio and determine the most important actions I should take this week. Prioritize tasks based on business impact, risk prevention, revenue opportunities, upcoming renewals, stalled onboarding, executive engagement gaps, or unresolved customer issues. For each recommended action, explain why it matters and what outcome it is intended to achieve.",
        },
        {
          title: "Draft my weekly manager report",
          prompt:
            "Prepare a professional weekly customer success summary based on my recent customer activities. Include the most important customer interactions, risks identified, opportunities created, milestones achieved, renewals supported, expansions discussed, and proactive actions taken. Structure the report so it can easily be shared with my manager. Focus on business impact and strategic customer outcomes rather than task lists.",
        },
      ],
      loadingMessages: [
        "Thinking through…",
        "Analyzing data…",
        "Gathering insights…",
        "Processing request…",
      ],
      currentLoadingIndex: 0,
      loadingInterval: null,
    };
  },
  computed: {
    canSend() {
      return this.promptText.trim().length > 0 && !this.isStreaming;
    },
    hasInput() {
      return this.promptText.length > 0;
    },
  },
  watch: {
    promptText() {
      this.$nextTick(() => this.autoGrow());
    },
    conversationHistory: {
      handler() {
        this.scrollToBottom();
      },
      deep: true,
    },
    streamingContent() {
      this.scrollToBottom();
    },
    isStreaming: {
      handler(newVal) {
        if (newVal && !this.streamingContent && !this.currentToolCall) {
          this.startLoadingRotation();
        } else {
          this.stopLoadingRotation();
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleEnter(event) {
      if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }
      event.preventDefault();
      this.sendMessage();
    },
    autoGrow() {
      const ta = this.$refs.textarea;
      if (!ta) return;
      ta.style.height = "0px";
      const next = Math.min(ta.scrollHeight, TEXTAREA_MAX_HEIGHT);
      ta.style.height = `${next}px`;
      ta.style.overflowY =
        ta.scrollHeight > TEXTAREA_MAX_HEIGHT ? "auto" : "hidden";
    },
    async sendMessage() {
      if (!this.canSend) return;
      const userMessage = this.promptText;
      this.promptText = "";
      await this.send(userMessage);
    },
    startNewChat() {
      this.showNewChatConfirm = true;
    },
    async confirmNewChat() {
      this.showNewChatConfirm = false;
      await this.clear();
      this.promptText = "";
    },
    usePromptTemplate(template) {
      this.promptText = template;
      this.$nextTick(() => {
        this.$refs.textarea?.focus();
      });
    },
    scrollToBottom() {
      if (this.$refs.chatBody) {
        this.$nextTick(() => {
          const element = this.$refs.chatBody;
          element.scrollTop = element.scrollHeight;
        });
      }
    },
    startLoadingRotation() {
      this.currentLoadingIndex = 0;
      this.stopLoadingRotation();
      this.loadingInterval = setInterval(() => {
        this.currentLoadingIndex =
          (this.currentLoadingIndex + 1) % this.loadingMessages.length;
      }, 2500);
    },
    stopLoadingRotation() {
      if (this.loadingInterval) {
        clearInterval(this.loadingInterval);
        this.loadingInterval = null;
      }
    },
  },
  components: { StarIcon, ActionModals },
  setup() {
    const buildRequestArgs = () => ({ scope: "global" });
    return {
      ...useAiChat({
        buildRequestArgs,
        fetchHistory: fetchGlobalChatHistory,
        clearHistory: clearGlobalChat,
      }),
      ...useActionModals({ buildRequestArgs }),
    };
  },
  mounted() {
    this.loadHistory();
    this.$nextTick(() => this.autoGrow());
  },
  beforeUnmount() {
    this.stopLoadingRotation();
  },
};
</script>
